---
title: "An Entirely New Git Hosting Network"
description: "Entire is a fast, distributed, Git-compatible network for mirroring GitHub repositories so agents can clone fast without hitting origin rate limits."
category: Company
author: Thomas Dohmke
image: /blog/an-entirely-new-git-hosting-network/og.png
featured: true
featureImage: /blog/an-entirely-new-git-hosting-network/feature.png
featureImageAlt: "Introducing Distributed Git Hosting"
---

{/* Editorial update notice, not a section title. */}
{/* lint ignore no-emphasis-as-heading */}

_This blog was modified slightly on Aug 28, 2026._

Git was always meant to be decentralized. Every clone contains a complete copy of the repository and its history, allowing software to be replicated across many hosts rather than controlled by a single server. But in practice, Git hosting has routed developers into centralized platforms built for a human-paced loop where a developer clones a repository once, works locally for a few hours, and makes the occasional push. This was sustainable until agents came along, sending thousands of concurrent requests in seconds, triggering traffic caps, and exposing failure points.

{/* Intentional emphasis on the launch thesis. */}
{/* lint ignore no-emphasis-as-heading */}

**At Entire, we believe that Git hosting must return to its original promise: a truly distributed network, not a system where the world’s software lives in a single location. So we built just that.**

[Starting today,](https://entire.io) you can mirror your public or private GitHub repositories on Entire: a fast, distributed, Git-compatible network. If your repo stays on GitHub, your coding agents fetch from a regional Entire cell that absorbs the heavy concurrent read traffic, so they clone fast without hitting the origin repo’s rate limits. When you push a branch that is backed by GitHub, the push will be as fast as GitHub allows, but we also support Entire-native branches that can absorb much more concurrency.

We are launching with all existing Entire users and regions in the US, EU, and Australia (note: at time of update, India is also available). New users will join a [waitlist](https://entire.io) so we can monitor traffic and load, then roll off progressively as we reach full capacity.

## A Fast, Distributed Architecture Built for Agent Scale

Entire runs distributed nodes across multiple regions and jurisdictions, letting users pin their data to a single region or spread of multiple regions for higher redundancy and scale. In early testing, Entire’s Git network has shown strong results.

**Git Hosting Benchmark Stats**

<img
  src="/blog/an-entirely-new-git-hosting-network/git-hosting-stats.png"
  alt="Git hosting benchmark stats"
  width={1920}
  height={1080}
/>

We measured our numbers with [ForgeMark](https://github.com/entireio/forgemark), a new open-source tool we’re releasing today under the MIT license. It benchmarks concurrent git-push throughput and latency against smart-HTTP forges under load patterns inspired by agent fleets, specifically for the following variables: workload, concurrency, repository size, branch type, comparison systems.

For workloads that need maximum throughput, any branch prefixed with `entire/unmirrored/` stays local to the region it’s pushed to instead of syncing out to the mirror. That direct path is what powers the ForgeMark numbers above. It gives you a route that stays up independent of the mirror and handles high-volume write workloads at full speed.

Entire is a Git-compatible repository network with a global control plane for identity and placement, and regional data planes for content-addressed Git storage. This design is optimized for:

- **Scale:** Entire scales Git traffic across regional API nodes and distributed object-storage providers. Ref updates remain protected by compare-and-set semantics, while object writes fan out across storage nodes, making the system well-suited to high-volume branch-parallel agent workloads.
- **Low Latency:** Agents depend on fast repository reads: refs, commits, files, diffs, and merge bases. Entire serves those operations from the selected regional cell, keeping feedback loops close to the compute doing the work.
- **Regional Control:** Entire stores and replicates repository content in the region the user chooses. The global control plane coordinates identity, access, and placement, but does not become the central store for repository content.
- **Availability:** Writes are replicated across multiple availability zones. If a node leaves or falls behind, repair and catchup flows restore the replica set so repositories stay available without users having to move data manually.

**In the coming months, we will decentralize and open source our Git network,** allow for self-hosting, and rapidly expand our own network around the world. This will enable every developer and organization alike to host in-region and build with speed and sovereignty.

## How To Start Mirroring Your Repo

The fastest way onto Entire is to [mirror a repository](https://docs.entire.io/overview) you already have on GitHub. With mirroring, you can continue to use GitHub exactly as you do today; or you and your agents can clone, fetch, and push to Entire.

<CloudflareStreamVideo
  videoId="08f18bdf4e13f6ff81752676ae6d40d4"
  title="Mirroring a repository with Entire"
  aspectRatioPercent="58.50487540628385%"
/>

### 1. Install the Entire GitHub App

A GitHub org admin or owner of a personal repo installs the [Entire GitHub App](https://github.com/apps/entire) and grants it access to the repositories they want to mirror.

### 2. Install the Entire CLI

```shell
curl -fsSL https://entire.io/install.sh | bash
```

```shell
brew install --cask entireio/tap/entire
```

### 3. Log In

```shell
entire login
```

### 4. Create the Mirror

Run the interactive mirror flow:

```shell
entire repo mirror create
```

Entire lists the GitHub repositories you can mirror, then asks which repo to mirror:

```text
Signed in as user (us) via https://us.auth.entire.io
  ✓ Fetching available repos

  Select repos to mirror
  Space to select, enter to confirm.

    [ ] entirehq/entire
  > [•] entireio/cli
```

Then choose the regions to mirror into:

```text
✓ Fetching regions

  Select regions to mirror into
  Each repo is mirrored into every selected region.

  > [•] us-east (us)
    [•] eu-west (eu)
```

Entire creates one mirror per selected repository and region:

```text
entireio/cli @ aws-us-east-2.entire.io          ✓ ready
entireio/cli @ aws-eu-central-1.entire.io       ✓ ready

 REPO          REGION        STATUS  CLONE URL
  entireio/cli  us-east (us)  ready   entire://aws-us-east-2.entire.io/gh/entireio/cli
  entireio/cli  eu-west (eu)  ready   entire://aws-eu-central-1.entire.io/gh/entireio/cli
```

### 5. Clone from the Regional Mirror

Once the mirror is ready, clone from Entire:

```shell
entire repo clone /gh/entireio/cli
```

If the repo is mirrored in more than one region, Entire prompts you to choose one. You can also clone a specific regional mirror directly:

```shell
git clone entire://aws-us-east-2.entire.io/gh/OWNER/REPO
```

The `entire://` scheme is handled by our Entire [gitremote-helper](https://git-scm.com/docs/gitremote-helpers), which ships with the Entire CLI.

## What’s Next

We’re continuing to build toward an open, independent, and decentralized platform for humans and agents to build software together. Next, we’re working on:

- Native hosting for public and private repositories on Entire’s network.
- An open-source Entire Git backend, with support for self-hosted nodes.
- Tamper-evident branch history and policy-as-code branch protection that any client can independently verify.
- CI/CD pipelines and infrastructure.
- More sign-in options, organization management, and enterprise policies.
- A developer workflow built around intent-based review.

To stay up to date, follow us on [X](https://x.com/goentire) and join our [Discord](https://discord.gg/jZJs3Tue4S). Your feedback and contributions are always welcome.
